import { Check, ChevronDown, ChevronRight, ChevronUp, Maximize2, TriangleAlert, X } from "lucide-react";
import { CSSProperties, useMemo } from "react";
import { useNavigate } from "react-router";

import { StatusPill, type StatusPillTone } from "nazare-ui/components";
import { useSortableRows } from "nazare-ui/utils";

import { runViewTransition } from "../../../run-view-transition";
import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../scroll-shadow";
import { SkeletonBlock } from "../../../skeleton-block";
import { InfiniteScrollSentinel, SkeletonRow } from "../../../skeleton-row";
import { useInfiniteRows } from "../../../use-infinite-rows";
import { useScrollEdges } from "../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../use-simulated-load";
import styles from "./recent-runs.module.css";

/**
 * Static mock data + presentation for the bottom half of the QA automation
 * dashboard (`/admin/qa-automation`): the "5 slowest tests" / "Tests with
 * the highest failure rate" ranked cards, and the "Recent runs" table with
 * its pagination footer. No backend exists for this in `platform/` yet — all
 * data below is hardcoded, shaped like the real BFF response.
 */

// ─────────────────────────────────────────────────────────────────────────
// Ranked lists — "5 slowest tests" / "Tests with the highest failure rate"
// ─────────────────────────────────────────────────────────────────────────

type RankedRow = {
  primary: string;
  context: string;
  primaryValue: string;
  secondaryValue: string;
  /** 0..1, how far the inline bar fills. */
  barFraction: number;
  barTone: "duration" | "fail";
};

const SLOWEST_TESTS: RankedRow[] = [
  {
    primary: "Deal pipeline — bulk stage transition for 200 companies",
    context: "desktop › pipeline › bulk actions",
    primaryValue: "42.8s",
    secondaryValue: "18 runs",
    barFraction: 1,
    barTone: "duration",
  },
  {
    primary: "Portfolio CSV export — 5k row workbook",
    context: "desktop › portfolio › export",
    primaryValue: "38.1s",
    secondaryValue: "24 runs",
    barFraction: 38.1 / 42.8,
    barTone: "duration",
  },
  {
    primary: "Fund report — PDF render with embedded charts",
    context: "desktop › reports › fund",
    primaryValue: "31.6s",
    secondaryValue: "12 runs",
    barFraction: 31.6 / 42.8,
    barTone: "duration",
  },
  {
    primary: "Company profile — attach 20 documents",
    context: "desktop › company › documents",
    primaryValue: "27.4s",
    secondaryValue: "30 runs",
    barFraction: 27.4 / 42.8,
    barTone: "duration",
  },
  {
    primary: "Investor onboarding — KYC document upload",
    context: "mobile › onboarding › kyc",
    primaryValue: "24.9s",
    secondaryValue: "15 runs",
    barFraction: 24.9 / 42.8,
    barTone: "duration",
  },
];

const HIGHEST_FAILURE_RATE: RankedRow[] = [
  {
    primary: "SSO login — Okta redirect after session timeout",
    context: "web › auth › sso",
    primaryValue: "42%",
    secondaryValue: "8/19 runs",
    barFraction: 0.42,
    barTone: "fail",
  },
  {
    primary: "Deal pipeline — drag card across virtualized columns",
    context: "desktop › pipeline › board",
    primaryValue: "31%",
    secondaryValue: "5/16 runs",
    barFraction: 0.31,
    barTone: "fail",
  },
  {
    primary: "Portfolio dashboard — live price ticker reconnect",
    context: "desktop › portfolio › ticker",
    primaryValue: "24%",
    secondaryValue: "4/17 runs",
    barFraction: 0.24,
    barTone: "fail",
  },
  {
    primary: "Fund report — chart legend overflow at 4k",
    context: "desktop › reports › fund",
    primaryValue: "18%",
    secondaryValue: "3/17 runs",
    barFraction: 0.18,
    barTone: "fail",
  },
  {
    primary: "Company profile — inline edit autosave race",
    context: "web › company › edit",
    primaryValue: "14%",
    secondaryValue: "2/14 runs",
    barFraction: 0.14,
    barTone: "fail",
  },
];

function RankedList({ rows }: { rows: RankedRow[] }) {
  return (
    <ol className={styles.rankedList}>
      {rows.map((row, idx) => (
        <li key={row.primary} className={styles.rankedRow} title={row.primary}>
          <span className={styles.rankedIndex}>{idx + 1}</span>
          <div className={styles.rankedMain}>
            <div className={styles.rankedPrimary}>{row.primary}</div>
            {row.context && <div className={styles.rankedContext}>{row.context}</div>}
            <div className={styles.rankedBarTrack}>
              <div
                className={
                  row.barTone === "fail"
                    ? `${styles.rankedBarFill} ${styles.rankedBarFail}`
                    : `${styles.rankedBarFill} ${styles.rankedBarDuration}`
                }
                style={{ width: `${Math.round(Math.max(0, Math.min(1, row.barFraction)) * 100)}%` }}
              />
            </div>
          </div>
          <div className={styles.rankedValues}>
            <div className={styles.rankedPrimaryValue}>{row.primaryValue}</div>
            <div className={styles.rankedSecondaryValue}>{row.secondaryValue}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Shimmering placeholder `<li>` shaped like a real `.rankedRow`, shown for
 * the initial ~500ms mount before the (static, always-available) ranked
 * data "loads in" — see `useSimulatedLoad`. Widths are varied per-field
 * (via `index`) so a card's 5 skeleton rows don't read as one uniform grid. */
function RankedRowSkeleton({ index }: { index: number }) {
  return (
    <li className={styles.rankedRow} aria-hidden="true">
      <span className={styles.rankedIndex}>{index + 1}</span>
      <div className={styles.rankedMain}>
        <SkeletonBlock height={13} width={`${62 + ((index * 13) % 30)}%`} />
        <div style={{ marginTop: 5 }}>
          <SkeletonBlock height={11} width={`${38 + ((index * 11) % 24)}%`} />
        </div>
        <div style={{ marginTop: 6 }}>
          <SkeletonBlock height={6} width="100%" radius="var(--radius-full)" />
        </div>
      </div>
      <div className={styles.rankedValues}>
        <SkeletonBlock height={13} width={40} />
        <div style={{ marginTop: 5 }}>
          <SkeletonBlock height={11} width={52} />
        </div>
      </div>
    </li>
  );
}

function RankedListSkeleton() {
  return (
    <ol className={styles.rankedList}>
      {Array.from({ length: 5 }, (_, index) => (
        <RankedRowSkeleton key={index} index={index} />
      ))}
    </ol>
  );
}

/**
 * Every "expand to a full page" affordance on this dashboard (the ranked
 * cards' "View all" footer link, each card's maximise icon button) navigates
 * to the SAME route for a given card — a real `<a href>` (not just an
 * onClick) so cmd/ctrl/middle-click all work, wrapped in `runViewTransition`
 * so the click still gets the shared-element morph instead of an instant
 * navigation, matching the Workflows dashboard's maximise convention
 * (`../workflows/summary-cards.tsx`).
 */
function useGoToFullPage(to: string) {
  const navigate = useNavigate();
  return (event: { preventDefault: () => void }) => {
    event.preventDefault();
    runViewTransition(() => navigate(to));
  };
}

/** Ghost icon button, pushed to the far right of a card's header — exact
 * copy of `../workflows/summary-cards.module.css`'s `.maximiseButton`
 * treatment, styled here as `.maximiseButton`/`.maximiseIcon` in
 * `recent-runs.module.css`. */
function MaximiseButton({ title, to }: { title: string; to: string }) {
  const goToFullPage = useGoToFullPage(to);
  return (
    <a
      href={to}
      onClick={goToFullPage}
      className={styles.maximiseButton}
      aria-label={`Expand ${title}`}
      title={`Expand ${title}`}
    >
      <Maximize2 className={styles.maximiseIcon} aria-hidden="true" />
    </a>
  );
}

function ViewAllFooter({ href }: { href: string }) {
  const goToFullPage = useGoToFullPage(href);
  return (
    <div className={styles.viewAllFooter}>
      <a className={styles.viewAllLink} href={href} onClick={goToFullPage}>
        <span>View all</span>
        <ChevronRight size={14} aria-hidden />
      </a>
    </div>
  );
}

function RankedCard({
  title,
  rows,
  emptyLabel,
  viewAllHref,
  viewTransitionName,
  isLoading,
}: {
  title: string;
  rows: RankedRow[];
  emptyLabel: string;
  viewAllHref: string;
  viewTransitionName: string;
  isLoading: boolean;
}) {
  return (
    <section
      className={styles.card}
      style={{ viewTransitionName } as unknown as CSSProperties}
    >
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{title}</h3>
        <MaximiseButton title={title} to={viewAllHref} />
      </div>
      {isLoading ? (
        <RankedListSkeleton />
      ) : rows.length > 0 ? (
        <RankedList rows={rows} />
      ) : (
        <p className={styles.emptyState}>{emptyLabel}</p>
      )}
      <ViewAllFooter href={viewAllHref} />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Recent runs table
// ─────────────────────────────────────────────────────────────────────────

type RunEnv = "dev" | "qa" | "prod" | "local" | "other";
type RunStatus = "passed" | "warned" | "failed";

type RunRow = {
  id: string;
  when: string;
  /** Minutes elapsed since the run — the real, comparable value the "When"
   * column sorts by (`when` itself is a display string like "yesterday",
   * not something meaningfully sortable as text). */
  ageMinutes: number;
  env: RunEnv;
  user: string;
  project: string;
  status: RunStatus;
  counts: { pass: number; warn: number; fail: number };
  duration: string;
  cost: string;
  tokensIn: string;
  tokensOut: string;
};

const RECENT_RUNS: RunRow[] = [
  {
    id: "run-1",
    when: "about 10 hours ago",
    ageMinutes: 600,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "desktop",
    status: "warned",
    counts: { pass: 36, warn: 12, fail: 0 },
    duration: "1h 12m 5s",
    cost: "~$4.82 est.",
    tokensIn: "506.5k",
    tokensOut: "14.8k",
  },
  {
    id: "run-2",
    when: "about 14 hours ago",
    ageMinutes: 840,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "mobile",
    status: "passed",
    counts: { pass: 52, warn: 0, fail: 0 },
    duration: "48m 21s",
    cost: "~$3.10 est.",
    tokensIn: "402.1k",
    tokensOut: "9.6k",
  },
  {
    id: "run-3",
    when: "yesterday",
    ageMinutes: 1440,
    env: "dev",
    user: "thomas@phoenixcourt.vc",
    project: "web",
    status: "failed",
    counts: { pass: 28, warn: 4, fail: 3 },
    duration: "36m 58s",
    cost: "~$2.44 est.",
    tokensIn: "298.7k",
    tokensOut: "7.2k",
  },
  {
    id: "run-4",
    when: "2 days ago",
    ageMinutes: 2880,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "admin-console",
    status: "passed",
    counts: { pass: 41, warn: 0, fail: 0 },
    duration: "58m 3s",
    cost: "~$3.67 est.",
    tokensIn: "455.2k",
    tokensOut: "11.1k",
  },
  {
    id: "run-5",
    when: "3 days ago",
    ageMinutes: 4320,
    env: "local",
    user: "priya@phoenixcourt.vc",
    project: "desktop",
    status: "warned",
    counts: { pass: 33, warn: 6, fail: 0 },
    duration: "44m 12s",
    cost: "~$2.91 est.",
    tokensIn: "331.4k",
    tokensOut: "8.4k",
  },
  {
    id: "run-6",
    when: "4 days ago",
    ageMinutes: 5760,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "billing",
    status: "passed",
    counts: { pass: 19, warn: 0, fail: 0 },
    duration: "22m 40s",
    cost: "~$1.38 est.",
    tokensIn: "168.9k",
    tokensOut: "4.1k",
  },
  {
    id: "run-7",
    when: "5 days ago",
    ageMinutes: 7200,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "mobile",
    status: "failed",
    counts: { pass: 47, warn: 2, fail: 5 },
    duration: "1h 4m 51s",
    cost: "~$5.29 est.",
    tokensIn: "544.0k",
    tokensOut: "13.9k",
  },
  {
    id: "run-8",
    when: "6 days ago",
    ageMinutes: 8640,
    env: "other",
    user: "marcus@phoenixcourt.vc",
    project: "onboarding",
    status: "passed",
    counts: { pass: 15, warn: 0, fail: 0 },
    duration: "12m 9s",
    cost: "~$0.87 est.",
    tokensIn: "97.3k",
    tokensOut: "2.6k",
  },
  {
    id: "run-9",
    when: "7 days ago",
    ageMinutes: 10080,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "desktop",
    status: "passed",
    counts: { pass: 22, warn: 0, fail: 0 },
    duration: "19m 47s",
    cost: "~$1.21 est.",
    tokensIn: "142.6k",
    tokensOut: "3.7k",
  },
  {
    id: "run-10",
    when: "8 days ago",
    ageMinutes: 11520,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "billing",
    status: "warned",
    counts: { pass: 30, warn: 3, fail: 0 },
    duration: "33m 15s",
    cost: "~$2.05 est.",
    tokensIn: "221.9k",
    tokensOut: "5.9k",
  },
  {
    id: "run-11",
    when: "9 days ago",
    ageMinutes: 12960,
    env: "dev",
    user: "priya@phoenixcourt.vc",
    project: "web",
    status: "passed",
    counts: { pass: 18, warn: 0, fail: 0 },
    duration: "15m 32s",
    cost: "~$0.98 est.",
    tokensIn: "108.4k",
    tokensOut: "2.9k",
  },
  {
    id: "run-12",
    when: "9 days ago",
    ageMinutes: 13200,
    env: "local",
    user: "thomas@phoenixcourt.vc",
    project: "mobile",
    status: "failed",
    counts: { pass: 24, warn: 1, fail: 2 },
    duration: "28m 06s",
    cost: "~$1.87 est.",
    tokensIn: "196.3k",
    tokensOut: "4.8k",
  },
  {
    id: "run-13",
    when: "10 days ago",
    ageMinutes: 14400,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "admin-console",
    status: "passed",
    counts: { pass: 45, warn: 0, fail: 0 },
    duration: "51m 40s",
    cost: "~$3.32 est.",
    tokensIn: "387.5k",
    tokensOut: "10.2k",
  },
  {
    id: "run-14",
    when: "11 days ago",
    ageMinutes: 15840,
    env: "other",
    user: "marcus@phoenixcourt.vc",
    project: "onboarding",
    status: "warned",
    counts: { pass: 20, warn: 5, fail: 0 },
    duration: "25m 18s",
    cost: "~$1.55 est.",
    tokensIn: "173.8k",
    tokensOut: "4.4k",
  },
  {
    id: "run-15",
    when: "12 days ago",
    ageMinutes: 17280,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "desktop",
    status: "passed",
    counts: { pass: 38, warn: 0, fail: 0 },
    duration: "42m 55s",
    cost: "~$2.78 est.",
    tokensIn: "312.1k",
    tokensOut: "8.1k",
  },
  {
    id: "run-16",
    when: "13 days ago",
    ageMinutes: 18720,
    env: "dev",
    user: "thomas@phoenixcourt.vc",
    project: "web",
    status: "failed",
    counts: { pass: 12, warn: 2, fail: 6 },
    duration: "18m 22s",
    cost: "~$1.14 est.",
    tokensIn: "121.7k",
    tokensOut: "3.2k",
  },
  {
    id: "run-17",
    when: "14 days ago",
    ageMinutes: 20160,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "billing",
    status: "passed",
    counts: { pass: 27, warn: 0, fail: 0 },
    duration: "31m 09s",
    cost: "~$1.94 est.",
    tokensIn: "204.6k",
    tokensOut: "5.3k",
  },
  {
    id: "run-18",
    when: "15 days ago",
    ageMinutes: 21600,
    env: "local",
    user: "priya@phoenixcourt.vc",
    project: "mobile",
    status: "warned",
    counts: { pass: 16, warn: 4, fail: 0 },
    duration: "14m 51s",
    cost: "~$0.91 est.",
    tokensIn: "97.8k",
    tokensOut: "2.5k",
  },
  {
    id: "run-19",
    when: "16 days ago",
    ageMinutes: 23040,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "admin-console",
    status: "passed",
    counts: { pass: 60, warn: 0, fail: 0 },
    duration: "1h 8m 47s",
    cost: "~$4.42 est.",
    tokensIn: "468.9k",
    tokensOut: "12.6k",
  },
  {
    id: "run-20",
    when: "17 days ago",
    ageMinutes: 24480,
    env: "other",
    user: "marcus@phoenixcourt.vc",
    project: "desktop",
    status: "passed",
    counts: { pass: 21, warn: 0, fail: 0 },
    duration: "20m 14s",
    cost: "~$1.28 est.",
    tokensIn: "138.2k",
    tokensOut: "3.5k",
  },
  {
    id: "run-21",
    when: "18 days ago",
    ageMinutes: 25920,
    env: "qa",
    user: "qa-bot@phoenixcourt.vc",
    project: "web",
    status: "failed",
    counts: { pass: 9, warn: 1, fail: 4 },
    duration: "11m 38s",
    cost: "~$0.72 est.",
    tokensIn: "76.4k",
    tokensOut: "2.0k",
  },
  {
    id: "run-22",
    when: "19 days ago",
    ageMinutes: 27360,
    env: "dev",
    user: "thomas@phoenixcourt.vc",
    project: "billing",
    status: "passed",
    counts: { pass: 33, warn: 0, fail: 0 },
    duration: "37m 26s",
    cost: "~$2.39 est.",
    tokensIn: "289.3k",
    tokensOut: "7.5k",
  },
  {
    id: "run-23",
    when: "20 days ago",
    ageMinutes: 28800,
    env: "local",
    user: "priya@phoenixcourt.vc",
    project: "mobile",
    status: "warned",
    counts: { pass: 25, warn: 3, fail: 0 },
    duration: "26m 03s",
    cost: "~$1.63 est.",
    tokensIn: "181.5k",
    tokensOut: "4.6k",
  },
  {
    id: "run-24",
    when: "21 days ago",
    ageMinutes: 30240,
    env: "prod",
    user: "release-bot@phoenixcourt.vc",
    project: "onboarding",
    status: "passed",
    counts: { pass: 17, warn: 0, fail: 0 },
    duration: "16m 40s",
    cost: "~$1.02 est.",
    tokensIn: "112.9k",
    tokensOut: "2.8k",
  },
];

const STATUS_META: Record<RunStatus, { label: string; tone: StatusPillTone }> = {
  passed: { label: "Passed", tone: "success" },
  warned: { label: "Warned", tone: "warning" },
  failed: { label: "Failed", tone: "error" },
};

/**
 * A busy QA pipeline realistically has more history than the 24 hand-authored
 * rows above — `TOTAL_RECENT_RUN_COUNT` is the real, accurate total this
 * table claims, extended out to it with deterministic synthetic rows for
 * infinite scroll. Same FNV-1a hash + mulberry32 PRNG technique as
 * `../workflows/pages/runs.tsx`'s `generateSyntheticRun`, so the SAME index
 * always produces the SAME synthetic row — no `Math.random()`/`Date.now()`.
 */
const TOTAL_RECENT_RUN_COUNT = 140;

function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const ENV_POOL: RunEnv[] = ["dev", "qa", "prod", "local", "other"];
const STATUS_POOL: RunStatus[] = ["passed", "passed", "passed", "warned", "failed"];
const USER_POOL = Array.from(new Set(RECENT_RUNS.map((run) => run.user)));
const PROJECT_POOL = Array.from(new Set(RECENT_RUNS.map((run) => run.project)));

/** "X days ago" for an age beyond what `Intl.RelativeTimeFormat`'s hour/day
 * granularity in `header-and-stats.tsx`'s `formatRelative` would bother
 * distinguishing — every synthetic row here is at least 22 days old (the
 * hand-authored rows already cover 0-21 days), so a flat "N days ago" is
 * both simpler and reads identically to the real thing at that distance. */
function formatAge(ageMinutes: number): string {
  const days = Math.floor(ageMinutes / (24 * 60));
  return `${days} days ago`;
}

const LAST_HARDCODED_AGE_MINUTES = RECENT_RUNS[RECENT_RUNS.length - 1].ageMinutes;

/** A run beyond the hand-authored `RECENT_RUNS` sample — deterministic per
 * `index`. Fixed 1-day step plus jitter smaller than the step, so age stays
 * strictly increasing (never a LATER row showing a MORE RECENT age than an
 * EARLIER row) — same technique as `runs.tsx`'s `generateSyntheticRun`. */
function generateSyntheticRun(index: number): RunRow {
  const rand = mulberry32(hashSeed(`qa-recent-run-${index}`));

  const rowsBeyond = index - RECENT_RUNS.length + 1;
  const stepMinutes = 24 * 60;
  const jitterMinutes = Math.floor(rand() * 600); // up to 10h, smaller than the 1-day step
  const ageMinutes = LAST_HARDCODED_AGE_MINUTES + rowsBeyond * stepMinutes + jitterMinutes;

  const status = STATUS_POOL[Math.floor(rand() * STATUS_POOL.length)];
  const pass = 8 + Math.floor(rand() * 50);
  const warn = status === "warned" ? 1 + Math.floor(rand() * 8) : 0;
  const fail = status === "failed" ? 1 + Math.floor(rand() * 6) : 0;
  const durationMinutes = 8 + Math.floor(rand() * 55);
  const durationSeconds = Math.floor(rand() * 60);
  const costDollars = (0.6 + rand() * 4.5).toFixed(2);
  const tokensInK = (60 + rand() * 480).toFixed(1);
  const tokensOutK = (1.5 + rand() * 13).toFixed(1);

  return {
    id: `run-synthetic-${index}`,
    when: formatAge(ageMinutes),
    ageMinutes,
    env: ENV_POOL[Math.floor(rand() * ENV_POOL.length)],
    user: USER_POOL[Math.floor(rand() * USER_POOL.length)],
    project: PROJECT_POOL[Math.floor(rand() * PROJECT_POOL.length)],
    status,
    counts: { pass, warn, fail },
    duration: `${durationMinutes}m ${String(durationSeconds).padStart(2, "0")}s`,
    cost: `~$${costDollars} est.`,
    tokensIn: `${tokensInK}k`,
    tokensOut: `${tokensOutK}k`,
  };
}

function getRecentRun(index: number): RunRow {
  return index < RECENT_RUNS.length ? RECENT_RUNS[index] : generateSyntheticRun(index);
}

function EnvPill({ env }: { env: RunEnv }) {
  return <span className={styles.envPill}>{env}</span>;
}

function CountsBreakdown({ counts }: { counts: RunRow["counts"] }) {
  return (
    <span className={styles.counts}>
      <Check size={11} strokeWidth={2.5} aria-hidden />
      {counts.pass}
      <span aria-hidden>·</span>
      <TriangleAlert size={11} strokeWidth={2.5} aria-hidden />
      {counts.warn}
      <span aria-hidden>·</span>
      <X size={11} strokeWidth={2.5} aria-hidden />
      {counts.fail}
    </span>
  );
}

function RecentRunsTable({ isLoading }: { isLoading: boolean }) {
  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const { loadedCount, isLoadingMore, hasMore, sentinelRef } = useInfiniteRows({
    totalCount: TOTAL_RECENT_RUN_COUNT,
    initialCount: RECENT_RUNS.length,
    pageSize: 20,
    scrollRootRef: tableWrapRef,
  });

  // Sorting/toggling applies to whatever's been loaded so far, same
  // load-then-sort convention as every other infinite-scroll table in this
  // app — it doesn't fetch ahead to re-sort rows beyond `loadedCount`.
  const loadedRuns = useMemo(
    () => Array.from({ length: loadedCount }, (_, index) => getRecentRun(index)),
    [loadedCount],
  );

  const { sorted, direction, toggle } = useSortableRows(
    loadedRuns,
    (run) => run.ageMinutes,
    "asc", // ageMinutes ascending = most recent first, matches the original static order
  );

  return (
    <section
      className={styles.recentRunsCard}
      style={{ viewTransitionName: "qa-card-recent-runs" } as unknown as CSSProperties}
    >
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Recent runs</h3>
        <MaximiseButton title="Recent runs" to="/qa-automation/recent-runs" />
      </div>
      <div className={styles.scrollShadowWrapper}>
        <div ref={tableWrapRef} className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <button
                    type="button"
                    className={styles.thSort}
                    onClick={toggle}
                    aria-label={`Sort by When, currently ${direction === "asc" ? "newest first" : "oldest first"}`}
                  >
                    When
                    {direction === "asc" ? (
                      <ChevronUp size={12} aria-hidden />
                    ) : (
                      <ChevronDown size={12} aria-hidden />
                    )}
                  </button>
                </th>
                <th>Env</th>
                <th>User</th>
                <th>Project</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Est. cost</th>
                <th aria-hidden />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }, (_, index) => (
                  <SkeletonRow key={index} columns={8} />
                ))
              ) : (
                <>
                  {sorted.map((run) => (
                    <tr key={run.id}>
                      <td>
                        <a className={styles.whenLink} href={`#${run.id}`}>
                          {run.when}
                        </a>
                      </td>
                      <td>
                        <EnvPill env={run.env} />
                      </td>
                      <td>
                        <span className={styles.userCell}>{run.user}</span>
                      </td>
                      <td className={styles.projectCell}>{run.project}</td>
                      <td>
                        <div className={styles.statusCell}>
                          <StatusPill tone={STATUS_META[run.status].tone}>
                            {STATUS_META[run.status].label}
                          </StatusPill>
                          <CountsBreakdown counts={run.counts} />
                        </div>
                      </td>
                      <td className={styles.durationCell}>{run.duration}</td>
                      <td>
                        <div className={styles.costCell}>{run.cost}</div>
                        <div className={styles.tokensCell}>
                          {run.tokensIn} in · {run.tokensOut} out
                        </div>
                      </td>
                      <td className={styles.actionCell}>
                        <a className={styles.viewLink} href={`#${run.id}`}>
                          View
                          <ChevronRight size={13} aria-hidden />
                        </a>
                      </td>
                    </tr>
                  ))}
                  {hasMore && <InfiniteScrollSentinel sentinelRef={sentinelRef} columns={8} />}
                  {isLoadingMore &&
                    Array.from({ length: 3 }, (_, index) => (
                      <SkeletonRow key={`skeleton-${index}`} columns={8} />
                    ))}
                </>
              )}
            </tbody>
          </table>
        </div>
        <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
      </div>
    </section>
  );
}

/**
 * "5 slowest tests" / "Tests with the highest failure rate" ranked cards
 * plus the "Recent runs" table — the bottom half of the QA automation
 * admin dashboard (`/admin/qa-automation`). Entirely static mock data; no
 * backend exists for this route in `platform/` yet.
 */
export function QaAutomationRecentRuns() {
  const isLoading = useSimulatedLoad();

  return (
    <div className={styles.stack}>
      <div className={styles.rankedGrid}>
        <RankedCard
          title="5 slowest tests"
          rows={SLOWEST_TESTS}
          emptyLabel="No test runs in the selected window."
          viewAllHref="/qa-automation/slowest-tests"
          viewTransitionName="qa-card-slowest-tests"
          isLoading={isLoading}
        />
        <RankedCard
          title="Tests with the highest failure rate"
          rows={HIGHEST_FAILURE_RATE}
          emptyLabel="No failures in the selected window."
          viewAllHref="/qa-automation/highest-failure-rate"
          viewTransitionName="qa-card-highest-failure-rate"
          isLoading={isLoading}
        />
      </div>
      <RecentRunsTable isLoading={isLoading} />
    </div>
  );
}
